import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { lblStyle, tableHead, tableCell } from "./waShared";
import { InfoTip } from "../components/InfoTip";

// ─── Admin Phones Whitelist (v119.57) ────────────────────────────────
// Any number listed here is treated as a manager — their voice messages
// trigger Voice Assistant / Voice→Meeting instead of customer sentiment.
// v119.73 — Per-phone personalization editor (tone + interests + notes).
// Used inline inside the admin phones table. The AI uses these to address
// the manager by name and weave their interests into replies.
const INTEREST_CHOICES = [
  { key: "poetry",             label: "📜 يحب الشعر العربي", color: "#7c2d12" },
  { key: "al_hilal",            label: "🦁 الهلال السعودي",   color: "#1e40af" },
  { key: "manchester_united",   label: "👹 مانشستر يونايتد",  color: "#dc2626" },
  { key: "data_driven",         label: "📊 يحب الأرقام",      color: "#0e7490" },
  { key: "concise",             label: "✂️ يفضّل الإيجاز",     color: "#374151" },
];

function PersonalizationEditor({ profile, onChange }) {
  const [local, setLocal] = useState({
    role_label: profile.role_label || "",
    tone:       profile.tone || "natural",
    interests:  profile.interests || [],
    notes:      profile.notes || "",
  });
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const toggleInterest = (key) => {
    const has = local.interests.includes(key);
    set("interests", has ? local.interests.filter((x) => x !== key) : [...local.interests, key]);
  };
  const save = () => onChange(local);

  // v119.77 — one-click "Apply Role Template" so the CEO can restore Talal's
  // poetry + Al-Hilal + Man Utd personality after deleting & re-adding him.
  // v119.78 — extended to HR + Finance roles.
  const applyTemplate = () => {
    const role = (profile.role || "").toLowerCase();
    if (role === "advisor") {
      setLocal({
        role_label: "المستشار الاستراتيجي",
        tone: "literary",
        interests: ["poetry", "al_hilal", "manchester_united"],
        notes: "أديب — يحب الشعر العربي. مشجّع نادي الهلال 🦁💙 وأيضاً Manchester United 👹. خاطبه بأسلوب أدبي راقٍ مع لمسات هلالية وأبيات شعر مناسبة للموضوع. يطلب تحليلات استراتيجية عميقة لا أرقاماً عابرة.",
      });
    } else if (role === "manager") {
      setLocal({
        role_label: "الرئيس التنفيذي",
        tone: "casual",
        interests: ["data_driven", "concise", "poetry"],
        notes: "يفضّل الردود السريعة بدون مقدمات. عامي سعودي.",
      });
    } else if (role === "hr") {
      setLocal({
        role_label: "مدير الموارد البشرية",
        tone: "natural",
        interests: ["concise"],
        notes: "يهتم بأداء الموظفين والحضور والانصراف وتدريب الباريستا.",
      });
    } else if (role === "finance") {
      setLocal({
        role_label: "المدير المالي",
        tone: "formal",
        interests: ["data_driven", "concise"],
        notes: "يركّز على الأرقام، الهوامش، التدفّقات النقدية، والتقارير المالية الدقيقة. يفضّل الردود المختصرة بالأرقام لا بالكلمات.",
      });
    } else if (role === "marketing") {
      setLocal({
        role_label: "مدير التسويق",
        tone: "natural",
        interests: ["data_driven"],
        notes: "يركّز على الحملات وقياس ROI ومعدّلات التحويل.",
      });
    } else if (role === "operations") {
      setLocal({
        role_label: "مدير العمليات",
        tone: "natural",
        interests: ["data_driven", "concise"],
        notes: "يركّز على السرعة والكفاءة وتقليل الهدر.",
      });
    } else {
      setLocal({ role_label: "", tone: "natural", interests: [], notes: "" });
    }
  };

  return (
    <div data-testid={`wa-personalize-editor-${profile.phone}`}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: "var(--brown-deep)" }}>
        🎨 شخصنة ردود الـAI لـ {profile.name}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={lblStyle}>اللقب/الدور المنطوق</label>
          <input className="sim-input" placeholder="مثل: المستشار الاستراتيجي"
            value={local.role_label} onChange={(e) => set("role_label", e.target.value)}
            data-testid={`wa-personalize-role-${profile.phone}`} />
        </div>
        <div>
          <label style={lblStyle}>أسلوب الرد</label>
          <select className="sim-input" value={local.tone} onChange={(e) => set("tone", e.target.value)}
            data-testid={`wa-personalize-tone-${profile.phone}`}>
            <option value="natural">طبيعي (افتراضي)</option>
            <option value="casual">عامية سعودية ودودة</option>
            <option value="formal">فصحى رسمية</option>
            <option value="literary">أدبي/شعري</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={lblStyle}>اهتمامات شخصية (الـAI يستخدمها بإبداع)</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {INTEREST_CHOICES.map((c) => {
            const sel = local.interests.includes(c.key);
            return (
              <button key={c.key} type="button" onClick={() => toggleInterest(c.key)}
                data-testid={`wa-personalize-int-${c.key}-${profile.phone}`}
                style={{
                  padding: "5px 10px", borderRadius: 999, fontSize: 11.5,
                  border: `1.5px solid ${sel ? c.color : "var(--border)"}`,
                  background: sel ? `${c.color}15` : "#fff",
                  color: sel ? c.color : "var(--brown-deep)",
                  fontWeight: 700, cursor: "pointer",
                }}>
                {sel ? "✓ " : ""}{c.label}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={lblStyle}>ملاحظات خاصة للـAI (اختيارية)</label>
        <textarea rows={2} className="sim-input"
          placeholder="مثال: يحب الاستشهاد بأقوال الملك سلمان، ابتعد عن المزاح الزائد"
          value={local.notes} onChange={(e) => set("notes", e.target.value)}
          data-testid={`wa-personalize-notes-${profile.phone}`} />
      </div>
      <button type="button" className="btn btn-dark" onClick={save}
        data-testid={`wa-personalize-save-${profile.phone}`}
        style={{ marginTop: 10, fontSize: 12 }}>
        💾 احفظ الشخصنة
      </button>
      <button type="button" onClick={applyTemplate}
        data-testid={`wa-personalize-apply-template-${profile.phone}`}
        style={{
          marginTop: 10, marginRight: 8, fontSize: 12, padding: "8px 14px",
          borderRadius: 8, border: "1.5px dashed var(--brown-deep)",
          background: "#fff7ed", color: "#7c2d12", fontWeight: 700, cursor: "pointer",
        }}
  aria-label="استرجاع التخصيص الافتراضي حسب الدور (مستشار/مدير)">
        🎨 استرجاع قالب الدور
      </button>
    </div>
  );
}

const WA_ROLE_LABELS = {
  ceo: "المدير التنفيذي", manager: "مدير", advisor: "مستشار",
  operations: "عمليات", marketing: "تسويق", branch_manager: "مدير فرع",
  staff_trainer: "موظفة شاملة", trainer: "مدربة ليلى",
};

function AdminPhonesPanel() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("manager");
  const [unlimited, setUnlimited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(-1);
  const [editIdx, setEditIdx] = useState(-1); // v119.217 — inline edit

  const load = useCallback(async () => {
    try {
      const r = await api.get("/whatsapp/admin-phones");
      setItems(r.data?.phones || []);
    } catch (_e) { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.put("/whatsapp/admin-phones", { phones: next });
      setItems(r.data?.phones || []);
      const rej = Array.isArray(r.data?.rejected) ? r.data.rejected : [];
      if (rej.length > 0) {
        toast.error(`تم رفض ${rej.length} رقم: ${rej[0].reason} (${rej[0].phone})`);
      } else {
        toast.success(`تم الحفظ — ${r.data?.count || 0} رقم في القائمة`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setSaving(false); }
  };

  const add = () => {
    const cleanPhone = phone.replace(/\s+/g, "").trim();
    const cleanName = name.trim();
    if (!cleanPhone || !cleanName) { toast.error("الاسم والرقم مطلوبان"); return; }
    if (!/^(\+?9665|05|5)\d{8}$/.test(cleanPhone)) {
      toast.error("رقم جوال سعودي غير صالح — مثال: 0561888444");
      return;
    }
    if (editIdx >= 0) {
      // v119.217 — edit in place, preserving personalization fields
      const next = items.map((p, i) => i === editIdx
        ? { ...p, phone: cleanPhone, name: cleanName, role, unlimited } : p);
      save(next);
      setEditIdx(-1);
    } else {
      save([...items, { phone: cleanPhone, name: cleanName, role, unlimited }]);
    }
    setPhone(""); setName(""); setRole("manager"); setUnlimited(false);
  };
  const startEdit = (idx) => {
    const p = items[idx];
    setName(p.name || "");
    setPhone((p.phone || "").replace(/^\++/, ""));
    setRole(p.role || "manager");
    setUnlimited(!!p.unlimited);
    setEditIdx(idx);
  };
  const cancelEdit = () => {
    setEditIdx(-1);
    setPhone(""); setName(""); setRole("manager"); setUnlimited(false);
  };
  const remove = (idx) => {
    if (!window.confirm(`حذف ${items[idx]?.name}؟`)) return;
    save(items.filter((_, i) => i !== idx));
  };
  const toggleUnlimited = (idx) => {
    const next = items.map((p, i) => i === idx ? { ...p, unlimited: !p.unlimited } : p);
    save(next);
  };
  // v122 — مفتاح مستقل: استلام التقارير المالية الدورية (يومي/أسبوعي/شهري).
  // منفصل تمامًا عن "بدون حدود"؛ افتراضيًا مطفأ للجميع.
  const toggleReports = (idx) => {
    const next = items.map((p, i) => i === idx
      ? { ...p, receive_periodic_reports: !p.receive_periodic_reports } : p);
    save(next);
  };

  return (
    <div className="card" style={{ padding: 14 }} data-testid="wa-admin-phones">
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "var(--brown-deep)" }}>
        👨‍💼 أرقام المدراء / الموظفين المخوّلين
      </div>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 14, lineHeight: 1.8 }}>
        أي رقم في هذه القائمة عند إرساله رسالة WhatsApp إلى رقم ثواني، يُعامَل كـ <b>طلب إداري</b>:
        <ul style={{ marginInlineStart: 20, marginTop: 6 }}>
          <li>🎙 رسالة صوتية طويلة → تُحوَّل إلى <b>محضر اجتماع</b> في صفحة «اجتماعات المدير».</li>
          <li>🎤 رسالة صوتية قصيرة أو نصية تبدأ بـ «كم/اعطني/تقرير» → <b>مساعد ذكي</b> يجيب بأرقام حية.</li>
          <li>♾️ <b>المستشار بدون حدود</b>: أي صوت/نص يصبح سؤال مفتوح للذكاء الاصطناعي مع سياق شامل (للمستشارين فقط).</li>
          <li>الرد يعود إلى نفس الرقم كرسالة WhatsApp عادية.</li>
        </ul>
        أي رقم خارج هذه القائمة يُعامَل كعميل (sentiment + رد آلي).
      </div>
      <div className="wa-admin-form" style={{ display: "grid", gap: 10, alignItems: "end" }}>
        <div>
          <label style={lblStyle}>الاسم</label>
          <input className="sim-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="خالد المدير" data-testid="wa-admin-name" />
        </div>
        <div>
          <label style={lblStyle}>رقم WhatsApp</label>
          <input className="sim-input" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="05XXXXXXXX" data-testid="wa-admin-phone" />
        </div>
        <div>
          <label style={lblStyle}>الدور</label>
          <select className="sim-input" value={role} onChange={(e) => setRole(e.target.value)} data-testid="wa-admin-role">
            <option value="ceo">المدير التنفيذي</option>
            <option value="manager">مدير</option>
            <option value="advisor">مستشار</option>
            <option value="operations">عمليات</option>
            <option value="marketing">تسويق</option>
            <option value="branch_manager">مدير فرع</option>
            <option value="staff_trainer">موظفة شاملة (خدمة عملاء + تدريب ليلى)</option>
            <option value="trainer">مدربة ليلى</option>
          </select>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
          padding: "8px 10px", borderRadius: 6, minHeight: 38,
          background: unlimited ? "linear-gradient(135deg,#fef3c7,#fde68a)" : "transparent",
          border: unlimited ? "1.5px solid #f59e0b" : "1px solid var(--border)",
          fontSize: 11.5, fontWeight: 700, color: unlimited ? "#78350f" : "var(--brown-deep)",
          whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)}
            data-testid="wa-admin-unlimited" />
          ♾️ بدون حدود
        </label>
        <button type="button" onClick={add} disabled={saving} className="btn btn-dark"
          style={{ fontSize: 12, minHeight: 38, width: "100%", whiteSpace: "nowrap" }} data-testid="wa-admin-add">
          ➕ إضافة
        </button>
      </div>

      <div style={{ marginTop: 14, overflowX: "auto" }}>
        {items.length === 0 ? (
          <div style={{ padding: 16, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>
            لا توجد أرقام إدارية بعد. أضف أول رقم ليبدأ المساعد الذكي بالعمل.
          </div>
        ) : (
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead style={{ background: "var(--beige)" }}>
              <tr>
                <th style={tableHead}>الاسم</th>
                <th style={tableHead}>الرقم</th>
                <th style={tableHead}>الدور</th>
                <th style={tableHead}>♾️ بدون حدود</th>
                <th style={tableHead}>📊 التقارير المالية الدورية</th>
                <th style={tableHead}>إجراء</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, i) => (
                <React.Fragment key={p.phone}>
                <tr style={{ borderTop: "1px solid var(--border)", background: editIdx === i ? "rgba(59,130,246,0.07)" : p.unlimited ? "rgba(245,158,11,0.06)" : "transparent" }}>
                  <td style={{ ...tableCell, fontWeight: 700 }}>{p.name}</td>
                  <td style={{ ...tableCell, direction: "ltr", fontFamily: "monospace" }}>{`+${(p.phone || "").replace(/^\++/, "")}`}</td>
                  <td style={tableCell}>{WA_ROLE_LABELS[p.role] || p.role}</td>
                  <td style={{ ...tableCell, textAlign: "center" }}>
                    <button type="button" onClick={() => toggleUnlimited(i)}
                      data-testid={`wa-admin-toggle-unlimited-${i}`}
                      style={{
                        padding: "3px 10px", borderRadius: 12, border: "none", cursor: "pointer",
                        background: p.unlimited ? "#16a34a" : "#e5e7eb",
                        color: p.unlimited ? "#fff" : "#6b7280",
                        fontSize: 11, fontWeight: 700,
                      }}>
                      {p.unlimited ? "✅ مفعّل" : "—"}
                    </button>
                  </td>
                  <td style={{ ...tableCell, textAlign: "center" }}>
                    <button type="button" onClick={() => toggleReports(i)}
                      data-testid={`wa-admin-toggle-reports-${i}`}
 
                      style={{
                        padding: "3px 10px", borderRadius: 12, border: "none", cursor: "pointer",
                        background: p.receive_periodic_reports ? "#16a34a" : "#e5e7eb",
                        color: p.receive_periodic_reports ? "#fff" : "#6b7280",
                        fontSize: 11, fontWeight: 700,
                      }} aria-label="عند التفعيل يستلم هذا الرقم التقارير المالية الدورية (يومي/أسبوعي/شهري). مطفأ افتراضيًا.">
                      {p.receive_periodic_reports ? "✅ مفعّل" : "—"}
                    </button>
                  </td>
                  <td style={tableCell}>
                    <button type="button" onClick={() => startEdit(i)}
                      data-testid={`wa-admin-edit-${i}`}
                      className="btn btn-sm" style={{ fontSize: 11, marginInlineEnd: 4 }}>
                      ✏️ تعديل
                    </button>
                    <button type="button" onClick={() => setExpanded(expanded === i ? -1 : i)}
                      data-testid={`wa-admin-personalize-${i}`}
                      className="btn btn-sm" style={{ fontSize: 11, marginInlineEnd: 4 }}>
                      {expanded === i ? "🔼" : "🎨 شخصنة"}
                    </button>
                    <button type="button" onClick={() => remove(i)}
                      className="btn btn-sm" style={{ fontSize: 11, background: "#fee2e2", color: "#991b1b", border: "none" }}>
                      🗑
                    </button>
                  </td>
                </tr>
                {expanded === i && (
                  <tr style={{ background: "var(--beige)" }}>
                    <td colSpan={5} style={{ padding: 12 }}>
                      <PersonalizationEditor profile={p} onChange={(patch) => {
                        const next = items.map((x, idx) => idx === i ? { ...x, ...patch } : x);
                        save(next);
                      }} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminPhonesPanel;
